import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import apiClient from '../api/client'
import { estadoDeEntrega } from '../entrega'
import ErrorAlert from '../components/ErrorAlert'
import LoadingSpinner from '../components/LoadingSpinner'
import { useAuth } from '../auth/useAuth'

interface Conversation {
  conversation_id?: string
  phone: string
  contact_name?: string | null
  last_message?: string
  status: string
  updated_at?: string
  last_message_ts?: string
  last_intent?: string
  assigned_to?: string | null
}

interface Message {
  message_id: string
  sender: 'user' | 'agent' | 'operator' | 'precal_cliente' | 'precal_bot'
  text: string
  timestamp: string
  delivery_status?: 'sent' | 'delivered' | 'read' | 'failed' | ''
  failure_code?: string
  failure_title?: string
  failure_msg?: string
  /** 'propio' | 'socio' | '': qué significa «enviado» (`entrega.ts`). */
  canal_envio?: string
}

interface ConversationDetail {
  conversation_id: string
  phone: string
  contact_name: string | null
  status: string
  messages: Message[]
  assigned_to?: string | null
  assigned_at?: string | null
  last_message_ts?: string | null
}

interface ConversationsResponse {
  conversations: Conversation[]
  count: number
}

const STATUS_COLORS: Record<string, { bg: string; color: string }> = {
  ACTIVE:  { bg: 'var(--c-brand-light)', color: 'var(--c-brand-hover)' },
  OPEN:    { bg: 'var(--c-brand-light)', color: 'var(--c-brand-hover)' },
  PENDING: { bg: 'var(--c-warn-light)',  color: 'var(--c-warn)' },
  CLOSED:  { bg: 'var(--c-surface-faint)',color: 'var(--c-ink-muted)' },
  ERROR:   { bg: 'var(--c-danger-light)',  color: 'var(--c-danger)' },
}

function initials(name?: string | null, phone?: string) {
  if (name) return name.split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase()
  return (phone ?? '??').slice(-2)
}

function fmtTime(ts?: string) {
  if (!ts) return ''
  const d = new Date(ts)
  const now = new Date()
  const sameDay = d.toDateString() === now.toDateString()
  return sameDay
    ? d.toLocaleTimeString('es-PE', { hour: '2-digit', minute: '2-digit' })
    : d.toLocaleDateString('es-PE', { day: '2-digit', month: 'short' })
}

/** Formatea la fecha de un mensaje como separador de grupo: "HOY", "AYER", "domingo 25 ago 2026", etc. */
function fmtDateSeparator(ts: string): string {
  const d = new Date(ts)
  if (Number.isNaN(d.getTime())) return ''
  const now = new Date()
  const dYMD = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`
  const nowYMD = `${now.getFullYear()}-${now.getMonth()}-${now.getDate()}`
  const yesterday = new Date(now)
  yesterday.setDate(now.getDate() - 1)
  const yYMD = `${yesterday.getFullYear()}-${yesterday.getMonth()}-${yesterday.getDate()}`
  if (dYMD === nowYMD) return 'HOY'
  if (dYMD === yYMD) return 'AYER'
  // Menos de 7 días: nombre del día
  const diffDays = Math.floor((now.getTime() - d.getTime()) / (1000 * 60 * 60 * 24))
  if (diffDays < 7) {
    return d.toLocaleDateString('es-PE', { weekday: 'long' })
      .replace(/^./, (c) => c.toUpperCase())
  }
  // Más de 7 días: fecha completa
  return d.toLocaleDateString('es-PE', { day: '2-digit', month: 'short', year: 'numeric' })
}

/** Extrae el "YYYY-MM-DD" de un timestamp para comparar días. */
function dayKey(ts: string): string {
  const d = new Date(ts)
  if (Number.isNaN(d.getTime())) return ''
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

function fmtPhone(phone?: string) {
  if (!phone) return ''
  // Formato +51 999 888 777 desde 51999888777 o 999888777
  const digits = phone.replace(/\D/g, '')
  const clean = digits.startsWith('51') && digits.length === 11 ? digits.slice(2) : digits
  if (clean.length === 9) {
    return `+51 ${clean.slice(0, 3)} ${clean.slice(3, 6)} ${clean.slice(6)}`
  }
  return `+${digits}`
}

/**
 * Formatea el preview del último mensaje con íconos para tipos de media.
 * Detecta prefijos [image], [document], [audio], [video] y muestra ícono + filename limpio.
 */
function fmtLastMessage(text?: string): { icon: string; label: string } {
  if (!text) return { icon: '', label: '—' }
  const mediaMatch = text.match(/^\[(image|document|audio|video|sticker)\]\s*(.*)/)
  if (mediaMatch) {
    const type = mediaMatch[1]
    let filename = mediaMatch[2].trim()
    // Limpiar wamid/hashes largos que no aportan al analista
    if (filename.startsWith('wamid.')) filename = 'archivo enviado'
    const icons: Record<string, string> = {
      image: '📷',
      document: '📄',
      audio: '🎤',
      video: '🎬',
      sticker: '💬',
    }
    const labels: Record<string, string> = {
      image: 'Imagen',
      document: 'Documento',
      audio: 'Audio',
      video: 'Video',
      sticker: 'Sticker',
    }
    return { icon: icons[type] ?? '📎', label: filename || labels[type] }
  }
  return { icon: '', label: text }
}

// Nombre público del analista para el saludo pre-cargado
function displayNameFromEmail(email?: string | null): string {
  if (!email) return 'un analista'
  const local = email.split('@')[0]
  const parts = local.split(/[._-]/).filter(Boolean)
  if (parts.length === 0) return local
  const capitalize = (w: string) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()
  return parts.map(capitalize).join(' ')
}

export default function Conversations() {
  const { canSupervise, user } = useAuth()
  const canReply = canSupervise
  const myEmail = user?.email?.toLowerCase() ?? ''

  const [conversations, setConversations] = useState<Conversation[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('ALL')

  const [selected, setSelected] = useState<Conversation | null>(null)
  const [detail, setDetail] = useState<ConversationDetail | null>(null)
  const [detailLoading, setDetailLoading] = useState(false)
  const [replyText, setReplyText] = useState('')
  const [sending, setSending] = useState(false)
  const [editingName, setEditingName] = useState(false)
  const [nameDraft, setNameDraft] = useState('')
  const [savingName, setSavingName] = useState(false)
  const [nameMsg, setNameMsg] = useState<string | null>(null)
  const [sendError, setSendError] = useState<string | null>(null)
  const [showTemplateBtn, setShowTemplateBtn] = useState(false)
  const [assigning, setAssigning] = useState(false)
  // El contenedor de los mensajes: se baja moviendo SU scroll (DEF-031).
  const mensajesRef = useRef<HTMLDivElement>(null)

  const fetchConversations = useCallback(async () => {
    setLoading(true); setError(null)
    try {
      const r = await apiClient.get<ConversationsResponse>('/conversations?limit=50')
      setConversations(r.data.conversations ?? [])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar conversaciones')
    } finally { setLoading(false) }
  }, [])

  useEffect(() => { void fetchConversations() }, [fetchConversations])

  const fetchDetail = useCallback(async (conv: Conversation) => {
    const id = conv.conversation_id
    if (!id) return
    setDetailLoading(true)
    try {
      const r = await apiClient.get<ConversationDetail>(`/conversations/${id}`)
      setDetail(r.data)
    } catch { setDetail(null) }
    finally { setDetailLoading(false) }
  }, [])

  useEffect(() => {
    if (selected) void fetchDetail(selected)
    else setDetail(null)
  }, [selected, fetchDetail])

  // Sin `scrollIntoView` (DEF-031, 07-oct-2026): ése mueve también la página,
  // y en horizontal, y dejaba el menú lateral fuera de la vista.
  useEffect(() => {
    const el = mensajesRef.current
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' })
  }, [detail?.messages])

  const saveName = async () => {
    if (!selected?.conversation_id || !nameDraft.trim()) return
    setSavingName(true); setNameMsg(null)
    try {
      const res = await apiClient.put<{ contact_name: string; drive?: { renamed: boolean } }>(
        `/conversations/${selected.conversation_id}`,
        { contact_name: nameDraft.trim() },
      )
      setDetail(prev => prev ? { ...prev, contact_name: res.data.contact_name } : prev)
      setEditingName(false)
      if (res.data.drive?.renamed) setNameMsg('Nombre actualizado y carpeta de Drive renombrada')
      else setNameMsg('Nombre actualizado')
      setTimeout(() => setNameMsg(null), 3000)
    } catch (e) {
      const err = e as { response?: { data?: { error?: string } } }
      setNameMsg(err?.response?.data?.error ?? 'Error al guardar')
    } finally { setSavingName(false) }
  }

  const handleReply = async () => {
    if (!replyText.trim() || !selected?.conversation_id) return
    const text = replyText.trim()
    setSending(true)
    setSendError(null)
    setReplyText('')
    // Optimistic update — show message immediately
    const optimisticMsg: Message = {
      message_id: `optimistic-${Date.now()}`,
      sender: 'operator',
      text,
      timestamp: new Date().toISOString(),
    }
    setDetail((prev) => prev ? { ...prev, messages: [...prev.messages, optimisticMsg] } : prev)
    try {
      await apiClient.post('/conversations/reply', {
        conversation_id: selected.conversation_id,
        text,
      })
      // Refetch after short delay to get server-confirmed message
      setTimeout(() => { void fetchDetail(selected) }, 1500)
    } catch (err) {
      const axiosErr = err as { response?: { status?: number; data?: { error?: string; message?: string; plantilla?: boolean } } }
      const status = axiosErr?.response?.status
      const errKind = axiosErr?.response?.data?.error
      const errMsg = axiosErr?.response?.data?.message
      // Remove optimistic message on error
      setDetail((prev) => prev
        ? { ...prev, messages: prev.messages.filter((m) => m.message_id !== optimisticMsg.message_id) }
        : prev)
      setReplyText(text)
      if (status === 409 && errKind === 'outside_window') {
        setSendError(errMsg ?? 'El cliente no ha respondido en 24 h. WhatsApp bloquea mensajes libres — solo se pueden enviar plantillas aprobadas o esperar a que escriba de nuevo.')
        // Con la sesión del socio no hay plantilla que ofrecer: el backend lo
        // dice en `plantilla` y en el mensaje (`_mensaje_ventana_cerrada`).
        setShowTemplateBtn(axiosErr?.response?.data?.plantilla !== false)
      } else {
        setSendError(errMsg ?? 'No se pudo enviar el mensaje. Intenta de nuevo.')
      }
    } finally { setSending(false) }
  }

  const handleSendTemplate = async () => {
    if (!selected?.conversation_id) return
    setSending(true); setSendError(null)
    try {
      const clientName = detail?.contact_name?.trim() || ''
      // Template inicial: "reengagement_pgh_v1" con 1 parámetro (nombre del cliente o cadena vacía)
      await apiClient.post('/conversations/reply-template', {
        conversation_id: selected.conversation_id,
        template_name: 'reengagement_pgh_v1',
        parameters: [clientName || 'estimado(a) cliente'],
      })
      setShowTemplateBtn(false)
      setTimeout(() => { void fetchDetail(selected) }, 1500)
    } catch (e) {
      const err = e as { response?: { status?: number; data?: { error?: string; message?: string } } }
      if (err?.response?.status === 503 && err?.response?.data?.error === 'templates_disabled') {
        setSendError('La plantilla aún no está registrada en Meta Business Manager. Registra "reengagement_pgh_v1" y espera la aprobación (24-48h). Luego activa el flag WHATSAPP_TEMPLATES_ENABLED en el backend.')
      } else {
        setSendError(err?.response?.data?.message ?? 'No se pudo enviar la plantilla')
      }
    } finally { setSending(false) }
  }

  const handleTake = async () => {
    if (!selected?.conversation_id || !detail) return
    setAssigning(true); setSendError(null)
    try {
      await apiClient.post(`/conversations/${selected.conversation_id}/assign`, {})
      // Después de asignar, cargar saludo pre-cargado en el input de respuesta
      const analyst = displayNameFromEmail(myEmail)
      const clientName = detail.contact_name?.trim()
      const greeting = clientName
        ? `Hola ${clientName}, soy ${analyst} de Prestamype. Te ayudaré con las consultas que tengas para tu préstamo con garantía hipotecaria.`
        : `Hola, soy ${analyst} de Prestamype. Te ayudaré con las consultas que tengas para tu préstamo con garantía hipotecaria.`
      setReplyText(greeting)
      void fetchDetail(selected)
      void fetchConversations()
    } catch (e) {
      const err = e as { response?: { data?: { error?: string; message?: string } } }
      setSendError(err?.response?.data?.message ?? err?.response?.data?.error ?? 'No se pudo tomar la conversación')
    } finally { setAssigning(false) }
  }

  const handleRelease = async () => {
    if (!selected?.conversation_id) return
    if (!window.confirm('¿Liberar esta conversación? Volverá a la cola para que otro analista la tome.')) return
    setAssigning(true); setSendError(null)
    try {
      await apiClient.post(`/conversations/${selected.conversation_id}/release`, {})
      void fetchDetail(selected)
      void fetchConversations()
    } catch (e) {
      const err = e as { response?: { data?: { error?: string; message?: string } } }
      setSendError(err?.response?.data?.message ?? 'No se pudo liberar')
    } finally { setAssigning(false) }
  }

  const filtered = useMemo(() => {
    let list = conversations
    if (statusFilter !== 'ALL') list = list.filter((c) => c.status === statusFilter)
    if (search.trim()) {
      const q = search.toLowerCase()
      list = list.filter((c) =>
        c.phone.toLowerCase().includes(q) || (c.last_message ?? '').toLowerCase().includes(q)
      )
    }
    return list
  }, [conversations, search, statusFilter])

  if (loading) return <LoadingSpinner label="Cargando conversaciones..." />
  if (error)   return <ErrorAlert message={error} onRetry={fetchConversations} />

  const chips = ['ALL', 'ACTIVE', 'OPEN', 'PENDING', 'CLOSED', 'ERROR']

  return (
    // Los costados se achican con la ventana (DEF-031): con 330 + 290 fijos, a
    // 1024 px el chat del medio quedaba en 112 px. Desde 1440 miden lo mismo
    // que antes. Los pisos bajaron a 200 y 180 el 07-oct-2026, con el mínimo
    // del portal en 768: ahí el área mide 648 y con 240 + 220 al chat le
    // quedaban 188 px; con 200 + 180, 268.
    <div style={{ display: 'grid',
                  gridTemplateColumns: 'clamp(200px, 26vw, 330px) minmax(0, 1fr) clamp(180px, 20vw, 290px)',
                  height: 'calc(100dvh - 175px)', minHeight: '520px',
                  background: 'white', border: '1px solid var(--c-surface-border)',
                  borderRadius: '16px', overflow: 'hidden' }}>

      {/* ── Columna 1: Lista ── */}
      <div style={{ borderRight: '1px solid var(--c-surface-border)', display: 'flex', flexDirection: 'column', minWidth: 0, minHeight: 0 }}>
        {/* Filtros */}
        <div style={{ padding: '14px 14px 10px', borderBottom: '1px solid var(--c-surface-border)',
                      display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <input
            type="search" placeholder="Buscar teléfono o mensaje..."
            value={search} onChange={(e) => setSearch(e.target.value)}
            style={{ border: '1px solid var(--c-surface-strong)', borderRadius: '10px',
                     padding: '8px 12px', fontSize: '12.5px', fontFamily: 'inherit', width: '100%', boxSizing: 'border-box' }}
          />
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
            {chips.map((s) => (
              <button key={s} onClick={() => setStatusFilter(s)}
                style={{ all: 'unset', cursor: 'pointer', fontSize: '11.5px', fontWeight: 600,
                         padding: '5px 11px', borderRadius: '20px',
                         border: `1px solid ${statusFilter === s ? 'var(--c-brand)' : 'var(--c-surface-strong)'}`,
                         background: statusFilter === s ? 'var(--c-brand)' : 'white',
                         color: statusFilter === s ? 'white' : 'var(--c-ink-muted)' }}>
                {s === 'ALL' ? 'Todos' : s}
              </button>
            ))}
          </div>
        </div>

        {/* Lista */}
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
          {filtered.length === 0 ? (
            <div style={{ padding: '32px 16px', textAlign: 'center', color: 'var(--c-ink-muted)', fontSize: '13px' }}>
              {search ? `Sin resultados para "${search}"` : 'No hay conversaciones'}
            </div>
          ) : filtered.map((conv) => {
            const id = conv.conversation_id ?? conv.phone
            const sc = STATUS_COLORS[conv.status] ?? STATUS_COLORS['CLOSED']
            const isSelected = selected?.conversation_id === conv.conversation_id
            const displayName = conv.contact_name?.trim() || fmtPhone(conv.phone)
            const showSubPhone = !!conv.contact_name?.trim()
            const preview = fmtLastMessage(conv.last_message ?? conv.last_intent)
            return (
              <div key={id} onClick={() => setSelected(conv)}
                style={{ padding: '12px 14px', display: 'flex', gap: '10px', cursor: 'pointer',
                         borderBottom: '1px solid var(--c-surface-faint)',
                         background: isSelected ? 'var(--c-surface-subtle)' : 'white' }}>
                <div style={{ width: '34px', height: '34px', borderRadius: '50%', flexShrink: 0,
                               background: 'var(--c-surface-border)', display: 'flex',
                               alignItems: 'center', justifyContent: 'center',
                               fontSize: '12px', fontWeight: 700, color: 'var(--c-ink-soft)' }}>
                  {initials(conv.contact_name, conv.phone)}
                </div>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '6px' }}>
                    <span style={{ fontSize: '13px', fontWeight: 700,
                                   overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {displayName}
                    </span>
                    <span style={{ fontSize: '10.5px', color: 'var(--c-ink-muted)', flexShrink: 0 }}>
                      {fmtTime(conv.last_message_ts ?? conv.updated_at)}
                    </span>
                  </div>
                  {showSubPhone && (
                    <div style={{ fontSize: '10.5px', color: 'var(--c-ink-muted)',
                                  fontFamily: "'IBM Plex Mono', monospace",
                                  margin: '1px 0 0' }}>
                      {fmtPhone(conv.phone)}
                    </div>
                  )}
                  <div style={{ fontSize: '12px', color: 'var(--c-ink-muted)', overflow: 'hidden',
                                textOverflow: 'ellipsis', whiteSpace: 'nowrap', margin: '2px 0 5px',
                                display: 'flex', alignItems: 'center', gap: '4px' }}>
                    {preview.icon && <span>{preview.icon}</span>}
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{preview.label}</span>
                  </div>
                  <span style={{ fontSize: '10px', fontWeight: 700, padding: '2px 7px', borderRadius: '20px',
                                 background: sc.bg, color: sc.color }}>
                    {conv.status}
                  </span>
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* ── Columna 2: Chat ── */}
      <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, minHeight: 0,
                    borderRight: '1px solid var(--c-surface-border)' }}>
        {!selected ? (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: 'var(--c-ink-muted)', fontSize: '13px' }}>
            Selecciona una conversación
          </div>
        ) : (
          <>
            {/* Chat header */}
            <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--c-surface-border)',
                          display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontSize: '14px', fontWeight: 700 }}>
                  {detail?.contact_name ?? fmtPhone(selected.phone)}
                </div>
                <div style={{ fontSize: '11.5px', color: 'var(--c-ink-muted)',
                              fontFamily: detail?.contact_name ? "'IBM Plex Mono', monospace" : undefined }}>
                  {detail?.contact_name ? fmtPhone(selected.phone) : selected.status}
                </div>
                {/* Estado de asignación */}
                {detail?.assigned_to && (
                  <div style={{ fontSize: '11px', marginTop: '4px', color: 'var(--c-ink-soft)' }}>
                    🧑‍💼 Atendida por{' '}
                    <strong>{detail.assigned_to.toLowerCase() === myEmail ? 'ti' : displayNameFromEmail(detail.assigned_to)}</strong>
                  </div>
                )}
              </div>
              {canReply && (
                <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                  {/* Botón Tomar / Liberar según estado de asignación */}
                  {!detail?.assigned_to && (
                    <button
                      onClick={() => void handleTake()}
                      disabled={assigning}
                      style={{ all: 'unset', cursor: assigning ? 'default' : 'pointer',
                               fontSize: '11.5px', fontWeight: 700,
                               padding: '7px 14px', borderRadius: '8px',
                               background: 'var(--c-brand)', color: 'white',
                               opacity: assigning ? 0.6 : 1 }}>
                      {assigning ? '…' : '👋 Tomar conversación'}
                    </button>
                  )}
                  {detail?.assigned_to && detail.assigned_to.toLowerCase() === myEmail && (
                    <button
                      onClick={() => void handleRelease()}
                      disabled={assigning}
                      style={{ all: 'unset', cursor: assigning ? 'default' : 'pointer',
                               fontSize: '11.5px', fontWeight: 600,
                               padding: '7px 12px', borderRadius: '8px',
                               border: '1px solid var(--c-surface-strong)', color: 'var(--c-ink-soft)',
                               opacity: assigning ? 0.6 : 1 }}>
                      Liberar
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Banner de error de envío (por ejemplo, ventana 24h Meta) */}
            {sendError && (
              <div style={{ background: 'var(--c-danger-light)', borderBottom: '1px solid var(--c-danger-border)',
                            padding: '10px 18px', display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                <span style={{ fontSize: '15px' }}>⚠️</span>
                <div style={{ flex: 1, fontSize: '12.5px', color: 'var(--c-danger-ink)', lineHeight: 1.5 }}>
                  {sendError}
                  {showTemplateBtn && (
                    <div style={{ marginTop: '8px' }}>
                      <button
                        onClick={() => void handleSendTemplate()}
                        disabled={sending}
                        style={{
                          all: 'unset', cursor: sending ? 'default' : 'pointer',
                          background: 'var(--c-brand)', color: 'white',
                          fontSize: '12px', fontWeight: 700,
                          padding: '6px 12px', borderRadius: '8px',
                          opacity: sending ? 0.6 : 1,
                        }}>
                        {sending ? 'Enviando…' : '🔄 Reactivar con plantilla'}
                      </button>
                    </div>
                  )}
                </div>
                <button onClick={() => { setSendError(null); setShowTemplateBtn(false) }}
                        style={{ all: 'unset', cursor: 'pointer', fontSize: '14px', color: 'var(--c-danger-ink)' }}>
                  ✕
                </button>
              </div>
            )}

            {/* Messages */}
            <div ref={mensajesRef} style={{ flex: 1, minHeight: 0, overflowY: 'auto', overscrollBehavior: 'contain', padding: '18px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {detailLoading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '32px' }}>
                  <LoadingSpinner label="Cargando mensajes..." />
                </div>
              ) : (detail?.messages ?? []).length === 0 ? (
                <p style={{ textAlign: 'center', color: 'var(--c-ink-muted)', fontSize: '13px' }}>
                  Sin mensajes en esta conversación.
                </p>
              ) : (() => {
                const visibleMessages = (detail?.messages ?? []).filter((msg) => {
                  const t = msg.text.trim()
                  if (!t.startsWith('[')) return true
                  if (/^\[(image|document|audio|video|sticker)\]/i.test(t)) return true
                  return false
                })
                let lastDay = ''
                return visibleMessages.map((msg) => {
                // Lo que el cliente le escribió al precalificador del socio es del cliente.
                const isUser = msg.sender === 'user' || msg.sender === 'precal_cliente'
                const isOperator = msg.sender === 'operator'
                const isPrecal = msg.sender === 'precal_bot'
                const media = fmtLastMessage(msg.text)
                const failed = msg.delivery_status === 'failed'
                const bg = failed
                  ? 'var(--c-danger)'   // rojo para mensajes que fallaron
                  : isUser ? 'var(--c-brand)'
                  : isOperator ? 'var(--c-info)'
                  : isPrecal ? 'var(--c-info-light)'
                  : 'var(--c-surface-subtle)'
                const color = (isUser || isOperator || failed) ? 'white' : 'var(--c-ink)'
                const senderLabel = isOperator ? 'analista'
                  : msg.sender === 'agent' ? 'bot'
                  : isPrecal ? 'precalificador del socio'
                  : msg.sender === 'precal_cliente' ? 'cliente · al precalificador'
                  : 'cliente'
                // El mismo estado, con las mismas palabras, que la bandeja
                // (`entrega.ts`, 03-nov-2026). Los ticks no decían nada cuando
                // faltaba el dato, y faltaba en la mitad de lo del bot.
                const entrega = estadoDeEntrega(msg)
                // Separador de fecha si el día cambió respecto al mensaje anterior
                const currentDay = dayKey(msg.timestamp)
                const showDaySeparator = currentDay && currentDay !== lastDay
                if (showDaySeparator) lastDay = currentDay
                const dayLabel = showDaySeparator ? fmtDateSeparator(msg.timestamp) : ''
                return (
                <React.Fragment key={msg.message_id}>
                  {showDaySeparator && dayLabel && (
                    <div style={{ display: 'flex', justifyContent: 'center', margin: '10px 0 4px' }}>
                      <span style={{
                        fontSize: '10.5px', fontWeight: 700, letterSpacing: '0.06em',
                        background: 'var(--c-surface-faint)', color: 'var(--c-ink-soft)',
                        padding: '4px 12px', borderRadius: '99px',
                        textTransform: 'uppercase',
                      }}>{dayLabel}</span>
                    </div>
                  )}
                  <div style={{ display: 'flex', justifyContent: isUser ? 'flex-end' : 'flex-start' }}>
                    <div style={{ maxWidth: 'min(70%, 600px)', overflowWrap: 'anywhere', padding: '9px 13px', borderRadius: '14px',
                                  background: bg, color }}>
                      <div style={{ fontSize: '13px', lineHeight: 1.45,
                                    display: 'flex', alignItems: 'center', gap: '6px' }}>
                        {media.icon && <span style={{ fontSize: '18px' }}>{media.icon}</span>}
                        <span>{media.icon ? media.label : msg.text}</span>
                      </div>
                      {/* Detalle del fallo si existe */}
                      {failed && (msg.failure_title || msg.failure_msg) && (
                        <div style={{ fontSize: '11px', marginTop: '5px', opacity: 0.95,
                                      background: 'var(--c-danger)', padding: '4px 8px', borderRadius: '6px',
                                      lineHeight: 1.3 }}>
                          <strong>No entregado:</strong> {msg.failure_title || 'error de Meta'}
                          {msg.failure_msg && <div style={{ marginTop: '2px', opacity: 0.9 }}>{msg.failure_msg}</div>}
                          {msg.failure_code && <div style={{ marginTop: '2px', fontSize: '9.5px', opacity: 0.8 }}>Código {msg.failure_code}</div>}
                        </div>
                      )}
                      <div style={{ fontSize: '9.5px', marginTop: '4px', opacity: 0.75,
                                    display: 'flex', alignItems: 'center', gap: '4px', justifyContent: 'flex-end' }}>
                        <span>{new Date(msg.timestamp).toLocaleTimeString('es-PE', { hour: '2-digit', minute: '2-digit' })}</span>
                        <span>· {senderLabel}</span>
                        {/* Del asesor Y del bot (DEF-002): un mensaje del bot
                            que Meta rechazó se veía como entregado. */}
                        {!isUser && entrega && (
                          <span
                            title={entrega.ayuda || undefined}
                            style={{ marginLeft: '2px',
                                     fontWeight: entrega.tono === 'tenue' ? 400 : 700 }}>
                            · {entrega.etiqueta}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </React.Fragment>
                )
              })
              })()}
            </div>

            {/* Reply box */}
            {canReply && (
              <div style={{ padding: '14px', borderTop: '1px solid var(--c-surface-border)', display: 'flex', gap: '10px', alignItems: 'flex-end' }}>
                <textarea
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void handleReply() } }}
                  placeholder="Escribir como agente… (Enter para enviar, Shift+Enter para nueva línea)"
                  rows={1}
                  style={{
                    flex: 1, border: '1px solid var(--c-surface-strong)', borderRadius: '10px',
                    padding: '10px 14px', fontSize: '13px', fontFamily: 'inherit',
                    boxSizing: 'border-box', resize: 'none', lineHeight: 1.5,
                    minHeight: 42, maxHeight: 120, overflowY: 'auto',
                  }}
                  onInput={(e) => {
                    const el = e.currentTarget
                    el.style.height = 'auto'
                    el.style.height = Math.min(el.scrollHeight, 120) + 'px'
                  }}
                />
                <button
                  onClick={() => void handleReply()}
                  disabled={sending || !replyText.trim()}
                  style={{ all: 'unset', cursor: 'pointer', background: 'var(--c-brand)',
                           color: 'white', fontWeight: 700, fontSize: '13px',
                           padding: '10px 18px', borderRadius: '10px', flexShrink: 0,
                           opacity: sending || !replyText.trim() ? 0.5 : 1 }}>
                  {sending ? 'Enviando…' : 'Enviar'}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* ── Columna 3: Contexto del lead ── */}
      <div style={{ padding: '18px', display: 'flex', flexDirection: 'column', gap: '14px', minHeight: 0,
                    overflowY: 'auto', background: 'var(--c-surface-alt)' }}>
        {!selected ? (
          <div style={{ color: 'var(--c-ink-muted)', fontSize: '12.5px', textAlign: 'center', marginTop: '32px' }}>
            Selecciona una conversación para ver el contexto
          </div>
        ) : (
          <>
            {/* Avatar + nombre */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px',
                          background: 'white', border: '1px solid var(--c-surface-border)',
                          borderRadius: '14px', padding: '14px' }}>
              <div style={{ width: '42px', height: '42px', borderRadius: '50%', flexShrink: 0,
                             background: 'var(--c-surface-border)', display: 'flex',
                             alignItems: 'center', justifyContent: 'center',
                             fontSize: '14px', fontWeight: 700, color: 'var(--c-ink-soft)' }}>
                {initials(detail?.contact_name, selected.phone)}
              </div>
              <div style={{ minWidth: 0, flex: 1 }}>
                {editingName ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <input
                      value={nameDraft}
                      onChange={e => setNameDraft(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') void saveName(); if (e.key === 'Escape') setEditingName(false) }}
                      placeholder="Nombre completo del cliente"
                      autoFocus
                      style={{
                        border: '1px solid var(--c-ink-ghost)', borderRadius: '6px',
                        padding: '5px 8px', fontSize: '13px', fontFamily: 'inherit', width: '100%', boxSizing: 'border-box',
                      }}
                    />
                    <div style={{ display: 'flex', gap: '5px' }}>
                      <button onClick={() => void saveName()} disabled={savingName || !nameDraft.trim()}
                        style={{
                          all: 'unset', cursor: savingName ? 'not-allowed' : 'pointer',
                          padding: '4px 10px', borderRadius: '5px', fontSize: '11px', fontWeight: 700,
                          background: 'var(--c-brand)', color: 'white',
                          opacity: savingName || !nameDraft.trim() ? 0.5 : 1,
                        }}>{savingName ? '…' : 'Guardar'}</button>
                      <button onClick={() => setEditingName(false)}
                        style={{
                          all: 'unset', cursor: 'pointer',
                          padding: '4px 10px', borderRadius: '5px', fontSize: '11px',
                          border: '1px solid var(--c-surface-strong)', color: 'var(--c-ink-soft)',
                        }}>Cancelar</button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div style={{
                      fontSize: '13.5px', fontWeight: 700, overflow: 'hidden',
                      textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      display: 'flex', alignItems: 'center', gap: '6px',
                    }}>
                      <span>{detail?.contact_name ?? selected.phone}</span>
                      {canReply && (
                        <button onClick={() => {
                          setNameDraft(detail?.contact_name ?? '')
                          setEditingName(true)
                        }} title="Editar nombre"
                          style={{
                            all: 'unset', cursor: 'pointer', fontSize: '11px', opacity: 0.55,
                            color: 'var(--c-info)',
                          }}>✎</button>
                      )}
                    </div>
                    {detail?.contact_name && (
                      <div style={{ fontSize: '11.5px', color: 'var(--c-ink-muted)', fontFamily: "'IBM Plex Mono', monospace" }}>
                        {selected.phone}
                      </div>
                    )}
                  </>
                )}
                {nameMsg && (
                  <div style={{ fontSize: '10.5px', color: 'var(--c-brand-hover)', marginTop: '4px' }}>
                    {nameMsg}
                  </div>
                )}
                {(() => {
                  const sc = STATUS_COLORS[selected.status] ?? STATUS_COLORS['CLOSED']
                  return (
                    <span style={{ fontSize: '10px', fontWeight: 700, padding: '2px 8px', borderRadius: '20px',
                                   background: sc.bg, color: sc.color, display: 'inline-block', marginTop: '4px' }}>
                      {selected.status}
                    </span>
                  )
                })()}
              </div>
            </div>

            {/* Última decisión del modelo */}
            {selected.last_intent && (
              <div style={{ background: 'white', border: '1px solid var(--c-surface-border)',
                            borderRadius: '14px', padding: '16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <svg width="14" height="14" viewBox="0 0 14 14">
                    <rect x="1" y="1" width="12" height="12" rx="3" fill="none" stroke="var(--c-purple)" strokeWidth="1.4"/>
                    <circle cx="7" cy="7" r="2.3" fill="var(--c-purple)"/>
                  </svg>
                  <span style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.06em', color: 'var(--c-ink-muted)' }}>
                    ÚLTIMA DECISIÓN
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12.5px' }}>
                  <span style={{ color: 'var(--c-ink-muted)' }}>Intención</span>
                  <span style={{ fontWeight: 600 }}>{selected.last_intent}</span>
                </div>
              </div>
            )}

            {/* Auditoría rápida */}
            <div style={{ background: 'white', border: '1px solid var(--c-surface-border)',
                          borderRadius: '14px', padding: '16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <svg width="14" height="14" viewBox="0 0 14 14">
                  <circle cx="7" cy="7" r="6" fill="none" stroke="var(--c-ink-muted)" strokeWidth="1.4"/>
                  <path d="M7 3.5V7l2.4 1.4" stroke="var(--c-ink-muted)" strokeWidth="1.3" fill="none" strokeLinecap="round"/>
                </svg>
                <span style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.06em', color: 'var(--c-ink-muted)' }}>
                  AUDITORÍA
                </span>
              </div>
              {(detail?.messages ?? []).slice(-3).reverse().map((m) => (
                <div key={m.message_id} style={{ display: 'flex', gap: '9px' }}>
                  <div style={{ width: '6px', height: '6px', borderRadius: '50%',
                                background: 'var(--c-brand)', marginTop: '5px', flexShrink: 0 }}/>
                  <div style={{ fontSize: '12px', lineHeight: 1.4 }}>
                    <span style={{ color: 'var(--c-ink-muted)' }}>
                      {new Date(m.timestamp).toLocaleTimeString('es-PE', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                    {' — '}{m.sender === 'user' ? 'Mensaje entrante'
                      : m.sender === 'operator' ? 'Respuesta del operador'
                      : m.sender === 'precal_cliente' ? 'Mensaje al precalificador del socio'
                      : m.sender === 'precal_bot' ? 'Respuesta del precalificador del socio'
                      : 'Respuesta del agente'}
                  </div>
                </div>
              ))}
              {(detail?.messages ?? []).length === 0 && (
                <div style={{ fontSize: '12px', color: 'var(--c-ink-muted)' }}>Sin eventos</div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  )
}
